import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiPut, operationsClient } from '@/lib/api/client';
import { customFieldEndpoints } from '@/lib/api/endpoints';
import { customFieldKeys } from '@/lib/queryKeys';
import type { CustomFieldValue, UpsertValuesDto } from '@/types/custom-fields';

interface UseUpsertValuesOptions {
  /**
   * When false, suppress BOTH the auto success toast and the auto error toast.
   * Used by callers that orchestrate a multi-step save (e.g. ContactFormPanel,
   * which fires `POST /contacts` then `PUT /custom-field-values` sequentially)
   * and want to emit one consolidated toast instead of two stacked ones — or
   * promote a custom-field failure to a "partial success" warning rather than
   * an error.
   */
  silent?: boolean;
}

export function useUpsertValues(options: UseUpsertValuesOptions = {}) {
  const queryClient = useQueryClient();
  const silent = options.silent ?? false;
  return useApiMutation<CustomFieldValue[], UpsertValuesDto>({
    mutationFn: (dto) =>
      apiPut<CustomFieldValue[]>(operationsClient, customFieldEndpoints.values(), dto),
    showErrorToast: !silent,
    onSuccess: (_data, variables) => {
      // Invalidate the whole values prefix for this entity type so the single detail
      // cache AND the table/bulk caches refresh after a write.
      void queryClient.invalidateQueries({
        queryKey: customFieldKeys.valuesBase(variables.entityType),
      });
      if (!silent) toast.success('Custom field values saved');
    },
  });
}
